import React, { useState, forwardRef, useImperativeHandle } from 'react';
import { getS3ObjectUrl } from 'libs/url';
import { useCouponSheetModal } from './useCouponSheetModal';
import { MobileBottomSheet } from './MobileBottomSheet';
import { LaptopModal } from './LaptopModal';

export interface CouponSheetModalReturnRef {
    showCoupon: () => void;
}

const emptyImageSrc = getS3ObjectUrl('/static/cart-coupon-modal-empty.png');

export interface CouponSheetModalPropTypes {
    couponId: string;
    shippingProfileId: string;
    couponSavingPrice?: Price;
    onChangeCouponId: (id: string | null) => void;
    isCartCalculating?: boolean;
}

export const CouponSheetModal = forwardRef<
    CouponSheetModalReturnRef,
    CouponSheetModalPropTypes & { isMobile: boolean }
>((props, ref) => {
    const {
        couponId,
        shippingProfileId,
        onChangeCouponId,
        couponSavingPrice,
        isCartCalculating,
        isMobile,
    } = props;

    const [visible, setVisible] = useState(false);

    const [manualCouponCode, setManualCouponCode] = useState<string>('');

    const {
        state: { error, isFetching, couponList, selectedCouponId },
        actions: {
            handleCouponCodeOnSubmit,
            handleSelectedCouponOnChange,
            resetCouponModalState,
        },
    } = useCouponSheetModal({
        visible,
        couponId,
        onChangeCouponId,
        shippingProfileId,
    });

    async function enhanceCouponCodeOnSubmit() {
        await handleCouponCodeOnSubmit(manualCouponCode, shippingProfileId);

        setManualCouponCode('');
    }

    useImperativeHandle(ref, () => ({
        showCoupon: () => {
            setVisible((prev) => !prev);
        },
    }));

    const handleOnClose = () => {
        resetCouponModalState();
        setVisible(false);
    };

    const Component = isMobile ? MobileBottomSheet : LaptopModal;

    return (
        <Component
            visible={visible}
            manualCouponCode={manualCouponCode}
            setManualCouponCode={setManualCouponCode}
            error={error}
            isFetching={isFetching}
            couponList={couponList}
            selectedCouponId={selectedCouponId}
            isCartCalculating={isCartCalculating}
            couponSavingPrice={couponSavingPrice}
            handleSelectedCouponOnChange={handleSelectedCouponOnChange}
            enhanceCouponCodeOnSubmit={enhanceCouponCodeOnSubmit}
            handleOnClose={handleOnClose}
            emptyImageSrc={emptyImageSrc}
        />
    );
});

CouponSheetModal.displayName = 'CouponSheetModal';
